import { formatMoney } from "@theme/money-formatting";
import { ThemeEvents } from "@theme/events";

const THEME_QTY_EVENT = "quantity-selector:update";

/** @type {MutationObserver | null} */
let stayaiObserver = null;

/**
 * Matches [`assets/product-form.js`](/Users/paul-antoine/PRANA-CHIA-US-shopify/assets/product-form.js) `#getSellingPlanValue` for the active checkout selling plan.
 * @param {HTMLElement} productForm
 * @returns {string}
 */
function getSellingPlanValueForButton(productForm) {
  const section =
    productForm.closest("[data-section-id], .shopify-section, section") ??
    productForm;

  const standard = /** @type {HTMLInputElement | null} */ (
    productForm.querySelector('[name="selling_plan"]')
  );
  if (standard?.value) return standard.value;

  const purchaseRadio = /** @type {HTMLInputElement | null} */ (
    section.querySelector('input[name="purchaseType"]:checked')
  );
  if (purchaseRadio?.value === "purchaseTypeSubscription") {
    const staySelect = /** @type {HTMLSelectElement | null} */ (
      section.querySelector('select[name="stayai_selling_plan"]')
    );
    if (staySelect?.value) return staySelect.value;
  }
  return "";
}

/**
 * Stay AI subscription row should follow the frequency dropdown (even when “Try once” is selected).
 * @param {HTMLElement} productForm
 * @returns {string}
 */
function getStayAiSellingPlanId(productForm) {
  const section =
    productForm.closest("[data-section-id], .shopify-section, section") ??
    productForm;
  const staySelect = /** @type {HTMLSelectElement | null} */ (
    section.querySelector('select[name="stayai_selling_plan"]')
  );
  return staySelect?.value || "";
}

/**
 * Parse the subscription discount percent from the "Subscribe & Save X%" label.
 * @returns {number} a fraction (e.g. 0.1 for 10%), or 0 when no discount is shown
 */
function getStayAiDiscountPercent() {
  const label = document.querySelector(
    ".stayai-purchase-label__wrapper .subscription-label, .subscription-label",
  );
  if (!label) return 0;
  // Ignore the frequency tag/savings spans — only read the headline label text.
  const text = label.childNodes[0]?.textContent ?? label.textContent ?? "";
  const match = text.match(/(\d+(?:\.\d+)?)\s*%/);
  if (!match) return 0;
  const pct = parseFloat(match[1]);
  if (!isFinite(pct) || pct <= 0 || pct >= 100) return 0;
  return pct / 100;
}

/**
 * @param {HTMLElement} priceLine
 * @returns {{ sellingPlans: Record<string, { p: number; c: number }> | null; baseUnit: number; baseCompare: number }}
 */
function parsePriceLine(priceLine) {
  /** @type {Record<string, { p: number; c: number }> | null} */
  let sellingPlans = null;
  try {
    if (priceLine.dataset.sellingPlans) {
      sellingPlans = JSON.parse(priceLine.dataset.sellingPlans);
    }
  } catch {
    /* no-op */
  }
  return {
    sellingPlans,
    baseUnit: parseInt(priceLine.dataset.unitPrice || "0", 10),
    baseCompare: parseInt(priceLine.dataset.comparePrice || "0", 10),
  };
}

/**
 * @param {HTMLElement} productForm
 * @param {HTMLElement} priceLine
 */
function syncStayAi(productForm, priceLine) {
  const { sellingPlans, baseUnit, baseCompare } = parsePriceLine(priceLine);
  if (!baseUnit) return;

  const qtyInput = /** @type {HTMLInputElement | null} */ (
    productForm.querySelector('input[name="quantity"]')
  );
  const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

  const currency = priceLine.dataset.currencyCode || "USD";
  const useCurrency = priceLine.dataset.useCurrency === "true";
  const fmt = useCurrency
    ? priceLine.dataset.moneyWithCurrencyFormat || "{{amount}}"
    : priceLine.dataset.moneyFormat || "{{amount}}";

  const oneTimeTotalCents = baseUnit * qty;

  // Subscription row: use selected frequency allocation (same cents as add-to-cart line when subscription is active).
  let planId = getStayAiSellingPlanId(productForm);
  if (!planId) {
    planId = getSellingPlanValueForButton(productForm);
  }

  let spAlloc = planId && sellingPlans ? sellingPlans[planId] : null;
  if (!spAlloc && sellingPlans) {
    const firstKey = Object.keys(sellingPlans)[0];
    if (firstKey) spAlloc = sellingPlans[firstKey];
  }

  const subUnitPrice = spAlloc ? spAlloc.p : baseUnit;
  const compareUnitPrice = spAlloc
    ? spAlloc.c > subUnitPrice
      ? spAlloc.c
      : 0
    : baseCompare;

  let subscriptionLineTotalCents = subUnitPrice * qty;
  let subscriptionCompareTotalCents = compareUnitPrice * qty;

  // No explicit compare price from the selling plan: the regular (one-time)
  // price is the scratch price, and the subscription price is that minus the
  // "Subscribe & Save X%" discount.
  if (subscriptionCompareTotalCents <= 0) {
    const discount = getStayAiDiscountPercent();
    if (discount > 0) {
      subscriptionCompareTotalCents = oneTimeTotalCents;
      subscriptionLineTotalCents = Math.round(oneTimeTotalCents * (1 - discount));
    }
  }

  document.querySelectorAll("[data-stayai-onetime-price]").forEach((el) => {
    el.textContent = formatMoney(oneTimeTotalCents, fmt, currency);
  });

  document
    .querySelectorAll("[data-stayai-subscription-price]")
    .forEach((el) => {
      if (subscriptionCompareTotalCents > subscriptionLineTotalCents) {
        el.innerHTML =
          formatMoney(subscriptionLineTotalCents, fmt, currency) +
          ' <s class="fullPrice">' +
          formatMoney(subscriptionCompareTotalCents, fmt, currency) +
          "</s>";
      } else {
        el.textContent = formatMoney(subscriptionLineTotalCents, fmt, currency);
      }
    });

  // Update the Add to Cart button price
  const isSubscription = getSellingPlanValueForButton(productForm) !== "";
  const buttonTotal = priceLine.querySelector('[data-ref="total"]');
  const buttonCompare = priceLine.querySelector('[data-ref="compare"]');

  if (buttonTotal) {
    if (isSubscription) {
      buttonTotal.textContent = formatMoney(
        subscriptionLineTotalCents,
        fmt,
        currency,
      );
      if (buttonCompare) {
        buttonCompare.textContent = formatMoney(
          subscriptionCompareTotalCents,
          fmt,
          currency,
        );
        buttonCompare.hidden = subscriptionCompareTotalCents <= 0;
      }
    } else {
      buttonTotal.textContent = formatMoney(oneTimeTotalCents, fmt, currency);
      if (buttonCompare) {
        buttonCompare.textContent = "";
        buttonCompare.hidden = true;
      }
    }
  }
}

/**
 * @param {{ productForm: HTMLElement }} ctx
 */
function runSync(ctx) {
  const priceLine = ctx.productForm.querySelector(".add-to-cart-text__price-line");
  if (!(priceLine instanceof HTMLElement)) return;

  if (stayaiObserver) stayaiObserver.disconnect();
  syncStayAi(ctx.productForm, priceLine);
  injectStayAiSubscriptionDetails();
  dedupeStayAiFrequencySelects();
  removeEmptyPumperQuantityGroups();
  connectStayAiObserver(ctx);
}

/**
 * @param {{ productForm: HTMLElement }} ctx
 */
function connectStayAiObserver(ctx) {
  const container = document.querySelector(".stayai-subscriptions");
  if (!container) return;
  if (!stayaiObserver) {
    stayaiObserver = new MutationObserver(() => runSync(ctx));
  }
  stayaiObserver.observe(container, { childList: true, subtree: true });
}

/**
 * @returns {{ productForm: HTMLElement } | null}
 */
function findScope() {
  if (!document.querySelector(".stayai-subscriptions")) return null;

  const pumper = document.querySelector("input.pumper_pair");
  if (pumper) {
    const root =
      pumper.closest("[data-section-id], .shopify-section, section, main") ||
      document;
    const pf = root.querySelector("product-form-component");
    const pl = pf?.querySelector?.(".add-to-cart-text__price-line");
    if (pf instanceof HTMLElement && pl instanceof HTMLElement) {
      return { productForm: pf };
    }
  }
  const pf = document.querySelector("product-form-component");
  const pl = pf?.querySelector?.(".add-to-cart-text__price-line");
  if (pf instanceof HTMLElement && pl instanceof HTMLElement) {
    return { productForm: pf };
  }
  return null;
}

/**
 * @param {{ productForm: HTMLElement }} ctx
 */
function bind(ctx) {
  const run = () => runSync(ctx);

  document.addEventListener(
    THEME_QTY_EVENT,
    (e) => {
      if (!(e.target instanceof Node) || !ctx.productForm.contains(e.target))
        return;
      run();
    },
    { passive: true },
  );

  const qtyInput = ctx.productForm.querySelector('input[name="quantity"]');
  let qtySetterTimer = null;
  if (qtyInput) {
    qtyInput.addEventListener("input", run);
    qtyInput.addEventListener("change", run);

    // Pumper sets .value programmatically (no event fired). Intercept the setter.
    const desc = Object.getOwnPropertyDescriptor(
      HTMLInputElement.prototype,
      "value",
    );
    if (desc && desc.set && desc.get) {
      const origSet = desc.set;
      const origGet = desc.get;
      Object.defineProperty(qtyInput, "value", {
        get() {
          return origGet.call(qtyInput);
        },
        set(v) {
          origSet.call(qtyInput, v);
          clearTimeout(qtySetterTimer);
          qtySetterTimer = setTimeout(run, 16);
        },
        configurable: true,
      });
    }
  }

  const section =
    ctx.productForm.closest("[data-section-id], .shopify-section, section") ??
    ctx.productForm;
  section.addEventListener(
    ThemeEvents.variantUpdate,
    () => {
      requestAnimationFrame(() => run());
    },
    { passive: true },
  );

  const totalEl = ctx.productForm.querySelector(
    '.add-to-cart-text__price-line [data-ref="total"]',
  );
  if (totalEl) {
    let moTimer = null;
    const lineMo = new MutationObserver(() => {
      clearTimeout(moTimer);
      moTimer = setTimeout(run, 50);
    });
    lineMo.observe(totalEl, {
      characterData: true,
      childList: true,
      subtree: true,
    });
  }

  document.addEventListener(
    "click",
    () => {
      requestAnimationFrame(() => requestAnimationFrame(run));
    },
    true,
  );

  // Pumper updates the quantity input asynchronously after the radio click.
  // Double-rAF is not long enough — use a short settle delay for pumper tier changes.
  document.addEventListener(
    "change",
    (e) => {
      if (
        e.target instanceof HTMLInputElement &&
        e.target.classList.contains("pumper_pair")
      ) {
        setTimeout(run, 80);
        setTimeout(run, 200);
      }
    },
    { passive: true },
  );

  const staySelect = section.querySelector(
    'select[name="stayai_selling_plan"]',
  );
  staySelect?.addEventListener("change", run);

  document.querySelectorAll(".pumper_horizontal_card").forEach((card) => {
    const pumperMo = new MutationObserver((mutations) => {
      if (
        mutations.some(
          (m) => m.type === "attributes" && m.attributeName === "class",
        )
      )
        run();
    });
    pumperMo.observe(card, { attributes: true, attributeFilter: ["class"] });
  });

  run();
}

function init() {
  let ctx = findScope();
  if (ctx) {
    bind(ctx);
    return;
  }
  const bodyObs = new MutationObserver(() => {
    ctx = findScope();
    if (ctx) {
      bodyObs.disconnect();
      bind(ctx);
    }
  });
  bodyObs.observe(document.body, { childList: true, subtree: true });
}

/**
 * Pumper sets `margin: 16px auto !important` inline — only JS setProperty can beat it.
 * Multiple elements with the same ID can exist (Pumper renders more than one widget
 * instance on the same page), so we use querySelectorAll instead of getElementById.
 * Loop guard: we check current values before writing so the MO we attach doesn't
 * trigger itself indefinitely.
 */
function zeroPumperMargins(widget) {
  const topOk =
    widget.style.getPropertyValue("margin-top") === "0px" &&
    widget.style.getPropertyPriority("margin-top") === "important";
  const bottomOk =
    widget.style.getPropertyValue("margin-bottom") === "0px" &&
    widget.style.getPropertyPriority("margin-bottom") === "important";

  if (!topOk) widget.style.setProperty("margin-top", "0px", "important");
  if (!bottomOk) widget.style.setProperty("margin-bottom", "0px", "important");
}

/**
 * Force margin-bottom: 0 on pumper_block__title elements via inline style so
 * the pmpr:!mb-5 Tailwind class is overridden regardless of CSS load order.
 * Observes child subtree so dynamically-rendered titles are caught too.
 */
function zeroPumperTitleMargins(root) {
  root
    .querySelectorAll(".pumper_block__title, .pumper_block__bottomtitle")
    .forEach((el) => {
      if (
        el.style.getPropertyValue("margin-bottom") !== "0px" ||
        el.style.getPropertyPriority("margin-bottom") !== "important"
      ) {
        el.style.setProperty("margin-bottom", "0px", "important");
      }
      if (el.classList.contains("pumper_block__bottomtitle")) {
        if (
          el.style.getPropertyValue("margin-top") !== "0px" ||
          el.style.getPropertyPriority("margin-top") !== "important"
        ) {
          el.style.setProperty("margin-top", "0px", "important");
        }
      }
    });
}

function setPumperBarsPerRow(widget) {
  widget.querySelectorAll(".pmpr_bars_grid").forEach((el) => {
    el.style.setProperty("--pmpr-bars-per-row", "4", "important");
  });
  // Remove min-height on #pumper_bundle_svelte (set inline with !important by Pumper)
  widget
    .querySelectorAll("#pumper_bundle_svelte, .pumper-bundles")
    .forEach((el) => {
      el.style.setProperty("min-height", "0", "important");
    });
}

function watchPumperWidgetMargins() {
  const attached = new WeakSet();

  const attach = (widget) => {
    if (attached.has(widget)) return;
    attached.add(widget);
    zeroPumperMargins(widget);
    zeroPumperTitleMargins(widget);
    setPumperBarsPerRow(widget);
    new MutationObserver(() => {
      zeroPumperMargins(widget);
      zeroPumperTitleMargins(widget);
      setPumperBarsPerRow(widget);
    }).observe(widget, {
      attributes: true,
      attributeFilter: ["style"],
      childList: true,
      subtree: true,
    });
  };

  // Handle all current instances (Pumper can render duplicate IDs).
  document.querySelectorAll('[id="svelte-bundle-widget"]').forEach(attach);

  // Watch for widgets injected after page load.
  new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.id === "svelte-bundle-widget") attach(node);
        node.querySelectorAll?.('[id="svelte-bundle-widget"]').forEach(attach);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
}

const STAYAI_SUB_DETAILS_HTML =
  '<div class="stayai-subscription-details-container">' +
  "<ul><li>Save 10% on every order</li><li>Skip or cancel anytime</li></ul>" +
  "</div>";

/**
 * Remove duplicate frequency options (same visible label), keeping the first.
 * @param {HTMLSelectElement} select
 */
function dedupeStayAiFrequencySelect(select) {
  const selected = select.options[select.selectedIndex];
  const selectedLabel =
    selected && selected.value !== "" ? selected.textContent.trim() : "";

  const seen = new Set();
  /** @type {HTMLOptionElement[]} */
  const toRemove = [];

  for (let i = 0; i < select.options.length; i++) {
    const option = select.options[i];
    const value = option.value;
    const label = option.textContent.trim();

    if (value === "" || label === "") continue;

    if (seen.has(label)) {
      toRemove.push(option);
    } else {
      seen.add(label);
    }
  }

  if (toRemove.length === 0) return;

  const selectedWasRemoved = toRemove.some(function (option) {
    return option === selected;
  });

  toRemove.forEach(function (option) {
    option.remove();
  });

  if (!selectedWasRemoved) return;

  const match = Array.from(select.options).find(function (option) {
    return option.value !== "" && option.textContent.trim() === selectedLabel;
  });

  if (match) {
    select.value = match.value;
    select.dispatchEvent(new Event("change", { bubbles: true }));
  }
}

function dedupeStayAiFrequencySelects() {
  document
    .querySelectorAll(
      "select.stayai-subscription-dropdown, select[name='stayai_selling_plan']",
    )
    .forEach(function (select) {
      if (select instanceof HTMLSelectElement) {
        dedupeStayAiFrequencySelect(select);
      }
    });
}

function isVarietyPacksProductPage() {
  return document.body.classList.contains("template-suffix-variety-packs");
}

const PUMPER_WIDGET_SELECTOR =
  "#svelte-bundle-widget, .pumper-bundles, .pmpr_bars_grid, .pumper_horizontal_card, input.pumper_pair";

/**
 * @param {ParentNode} root
 * @returns {boolean}
 */
function rootHasPumperWidgetUI(root) {
  return !!root.querySelector(PUMPER_WIDGET_SELECTOR);
}

/**
 * @param {Element} appBlock
 * @returns {boolean}
 */
function pumperAppBlockHasWidget(appBlock) {
  return rootHasPumperWidgetUI(appBlock);
}

/**
 * @param {Element} group
 * @returns {boolean}
 */
function groupHasRemovedPumperQuantityLabel(group) {
  return Array.from(
    group.querySelectorAll(":scope > .group-block-content > .text-block p"),
  ).some(function (text) {
    return text.textContent?.trim().toLowerCase() === "choose your quantity";
  });
}

function removeEmptyPumperQuantityGroups() {
  if (isVarietyPacksProductPage()) return;

  document
    .querySelectorAll(".product-details .group-block")
    .forEach(function (group) {
      if (group.hasAttribute("data-pumper-quantity-removed")) return;
      if (rootHasPumperWidgetUI(group)) return;

      const appBlock = group.querySelector(
        ':scope > .group-block-content > .shopify-app-block[id*="pumper_bundles"]',
      );
      const hasRemovedQuantityLabel = groupHasRemovedPumperQuantityLabel(group);
      if (!appBlock && !hasRemovedQuantityLabel) return;
      if (appBlock && pumperAppBlockHasWidget(appBlock)) return;

      group.setAttribute("data-pumper-quantity-removed", "");
      group.remove();
    });
}

function injectStayAiSubscriptionDetails() {
  document
    .querySelectorAll(
      '.stayai-purchase-label:has(#purchaseTypeSubscription) .stayai-purchase-label__wrapper, label.stayai-purchase-label[for="purchaseTypeSubscription"] .stayai-purchase-label__wrapper',
    )
    .forEach(function (wrapper) {
      if (wrapper.querySelector(".stayai-subscription-details-container"))
        return;
      const subscriptionBox = wrapper.querySelector(
        "[data-retextion-subscription-box]",
      );
      if (!subscriptionBox) return;
      subscriptionBox.insertAdjacentHTML("afterend", STAYAI_SUB_DETAILS_HTML);
    });
}

/** @type {ReturnType<typeof setTimeout> | null} */
let stayaiDomFixTimer = null;

function scheduleStayAiDomFixes() {
  if (stayaiDomFixTimer !== null) clearTimeout(stayaiDomFixTimer);
  stayaiDomFixTimer = setTimeout(function () {
    injectStayAiSubscriptionDetails();
    dedupeStayAiFrequencySelects();
    removeEmptyPumperQuantityGroups();
  }, 50);
}

function watchStayAiSubscriptionDetails() {
  injectStayAiSubscriptionDetails();
  dedupeStayAiFrequencySelects();
  new MutationObserver(function () {
    scheduleStayAiDomFixes();
  }).observe(document.body, { childList: true, subtree: true });
}

function watchEmptyPumperQuantityGroups() {
  removeEmptyPumperQuantityGroups();
  new MutationObserver(function () {
    removeEmptyPumperQuantityGroups();
  }).observe(document.body, { childList: true, subtree: true });
}

function boot() {
  watchEmptyPumperQuantityGroups();
  init();
  watchPumperWidgetMargins();
  watchStayAiSubscriptionDetails();
}

if (document.body) {
  removeEmptyPumperQuantityGroups();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot, { once: true });
} else {
  boot();
}
